import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { ConsentContext } from 'App/Providers/games/ConsentProvider';
import { LanguageContext } from 'App/Providers/games/LanguageProvider';
import { getInitialPushDimension, getPushDimension, PushContext } from 'App/Services/Notifications';
import { getPwaDimension, PwaContext } from 'App/Services/Pwa';

import { useDeferredAnalytics } from './hooks/useDeferredAnalytics';
import { useLandingAnalytics } from './hooks/useLandingAnalytics';

const AnalyticsContext = createContext(null);

/**
 * Shared analytics context: raw clients plus deferred send helpers.
 * Prefer {@link useAnalytics} over importing this context directly.
 *
 * @returns {{ga?: object, gameGA?: object, sendGameDesignEvent: () => void, sendGoogleBasicEvent: () => void, sendGoogleEvent: () => void}} - Raw analytics clients (once created) and deferred senders that queue events until the clients are ready.
 */
const useAnalytics = () => useContext(AnalyticsContext);

const createGameGAService = async ({
    key,
    secret,
    environment,
    buildVersion,
    landingName,
    dimensions,
    resources,
}) => {
    const { default: GameAnalyticsService } = await import('./game/GameAnalytics');

    return new GameAnalyticsService({
        gameKey: key,
        gameSecret: secret,
        build: buildVersion,
        immediateInit: false,
        landingName,
        environment,
        dimensions,
        resources,
    });
};

const createGoogleGAService = async ({ trackingId, landingName, environment }) => {
    const { default: GoogleAnalytics } = await import('./google/GA4');

    return new GoogleAnalytics({
        trackingId,
        landingName,
        immediateInit: false,
        gtagOptions: { debug_mode: environment !== 'production' },
    });
};

/**
 * Shared GA / GameAnalytics provider for game landings.
 *
 * Creates clients once consent and bootstrap prerequisites are met, seeds
 * push / PWA / bot custom dimensions, and keeps those dimensions in sync.
 * Exposes raw `{ ga, gameGA }` plus deferred
 * `{ sendGameDesignEvent, sendGoogleBasicEvent, sendGoogleEvent }` via
 * {@link useAnalytics}. Pending events are dropped if analytics
 * consent is denied or a client fails to init; they stay queued while consent
 * is still undecided. Nest under the landing `AppProvider`, `ConsentProvider`,
 * `PushProvider`, and `PwaProvider`.
 *
 * @param {object} props - Component props.
 * @param {import("react").Context<object>} props.appContext - The landing's `AppContext`. Must
 *   expose `isPageLoaded`, `gameConfig`, `analyticsConfig`, `offerConfigReady`,
 *   `botInfo.isBot`, and `appConfig.{ env, pushCampaignConfig }`.
 * @param {string} props.storageKey - Storage namespace for landing environment
 *   events (push prompt count).
 * @param {"main" | "mobile" | "desktop"} [props.lpVersion='main'] - Extra LP
 *   version event for mobile (`LP:MB`) and desktop (`LP:DT`).
 * @param {import("react").ReactNode} props.children - Landing tree that reads analytics through `useAnalytics`.
 * @returns {import("react").JSX.Element} - The context provider wrapping `children`.
 */
const AnalyticsProvider = ({ appContext, storageKey, lpVersion = 'main', children }) => {
    const [ga, setGA] = useState(undefined);
    const [gameGA, setGameGA] = useState(undefined);

    // Avoid repeating SDK updates for dimensions already seeded during init.
    const dimensionValuesRef = useRef({ push: undefined, pwa: undefined });

    // Withdrawing consent clears the client state, so the teardown effect
    // needs its own handle on the instances it has to shut down.
    const gaRef = useRef(undefined);
    const gameGARef = useRef(undefined);

    gaRef.current = ga;
    gameGARef.current = gameGA;

    const {
        isPageLoaded,
        gameConfig,
        analyticsConfig,
        offerConfigReady,
        botInfo: { isBot },
        appConfig: { env, pushCampaignConfig },
    } = useContext(appContext);
    const { consentBannerReady, analyticsConsent } = useContext(ConsentContext);
    const { locale } = useContext(LanguageContext);
    const { pwaStatus } = useContext(PwaContext);
    const { pushStatus, pushStatusReady } = useContext(PushContext);

    const gameBuildVersion = gameConfig?.buildVersion;
    const landingSource = pushCampaignConfig?.[locale]?.source;
    const analyticsEnabled = analyticsConsent === true;
    const activeGA = analyticsEnabled ? ga : undefined;
    const activeGameGA = analyticsEnabled ? gameGA : undefined;

    const { callGA, callGameGA, markClientFailed, resetClientFailure } = useDeferredAnalytics({
        ga: activeGA,
        gameGA: activeGameGA,
        analyticsConsent,
    });

    // Dropping the references is not enough: the GameAnalytics SDK is a
    // module-level singleton that keeps its session open and keeps flushing
    // stored events until it is told to stop.
    useEffect(() => {
        if (analyticsEnabled) return;

        gameGARef.current?.stop();

        if (gaRef.current) gaRef.current.enabled = false;

        setGA(undefined);
        setGameGA(undefined);
    }, [analyticsEnabled]);

    useEffect(() => {
        if (!isPageLoaded || !consentBannerReady || !offerConfigReady) return;
        // The dimension seeded below applies only to events sent after it, so
        // the push status has to be final rather than merely present.
        if (!pushStatus || !pushStatusReady) return;
        if (!pwaStatus || typeof isBot !== 'boolean') return;
        if (analyticsConsent !== true || gameGA) return;

        let cancelled = false;
        const pushDimension = getInitialPushDimension(pushStatus);
        const pwaDimension = getPwaDimension(pwaStatus);
        const { gameAnalyticsKey, gameAnalyticsSecret, dimensions, resources } = analyticsConfig;

        const initialDimensions = {
            values1: {
                ...dimensions.values1,
                current: pushDimension,
            },
            values2: {
                ...dimensions.values2,
                current: pwaDimension,
            },
            values3: {
                ...dimensions.values3,
                current: isBot ? 'is_bot' : 'is_not_bot',
            },
        };

        resetClientFailure('gameGA');

        createGameGAService({
            key: gameAnalyticsKey,
            secret: gameAnalyticsSecret,
            environment: env,
            buildVersion: gameBuildVersion,
            landingName: landingSource,
            dimensions: initialDimensions,
            resources,
        })
            .then((gameGAInstance) => {
                if (cancelled) return;

                const initialized = gameGAInstance.init();

                if (initialized) {
                    // Recorded only once the values are really on the client,
                    // so an init that never happened cannot make the sync
                    // effects below skip the first real update.
                    dimensionValuesRef.current = {
                        push: pushDimension,
                        pwa: pwaDimension,
                    };
                    setGameGA(gameGAInstance);
                } else {
                    markClientFailed('gameGA');
                }
            })
            .catch(() => {
                if (cancelled) return;

                markClientFailed('gameGA');
            });

        return () => {
            cancelled = true;
        };
    }, [
        isPageLoaded,
        consentBannerReady,
        offerConfigReady,
        pushStatus,
        pushStatusReady,
        pwaStatus,
        isBot,
        analyticsConsent,
        analyticsConfig,
        gameBuildVersion,
        landingSource,
        env,
        gameGA,
        markClientFailed,
        resetClientFailure,
    ]);

    useEffect(() => {
        if (!isPageLoaded || !consentBannerReady || !offerConfigReady) return;
        if (analyticsConsent !== true || ga) return;

        let cancelled = false;

        resetClientFailure('ga');

        createGoogleGAService({
            trackingId: analyticsConfig?.gaTrackId,
            landingName: landingSource,
            environment: env,
        })
            .then((gaInstance) => {
                if (cancelled) return;

                const initialized = gaInstance.init();

                if (initialized) {
                    setGA(gaInstance);
                } else {
                    markClientFailed('ga');
                }
            })
            .catch(() => {
                if (cancelled) return;

                markClientFailed('ga');
            });

        return () => {
            cancelled = true;
        };
    }, [
        isPageLoaded,
        consentBannerReady,
        offerConfigReady,
        analyticsConsent,
        analyticsConfig?.gaTrackId,
        landingSource,
        env,
        ga,
        markClientFailed,
        resetClientFailure,
    ]);

    useEffect(() => {
        if (!activeGameGA) return;

        const pushDimension = getPushDimension(pushStatus);

        if (pushDimension === undefined || pushDimension === dimensionValuesRef.current.push)
            return;

        dimensionValuesRef.current.push = pushDimension;
        activeGameGA.setCustomDimension1(pushDimension);
    }, [pushStatus, activeGameGA]);

    useEffect(() => {
        if (!activeGameGA) return;

        const pwaDimension = getPwaDimension(pwaStatus);

        if (pwaDimension === undefined || pwaDimension === dimensionValuesRef.current.pwa) return;

        dimensionValuesRef.current.pwa = pwaDimension;
        activeGameGA.setCustomDimension2(pwaDimension);
    }, [pwaStatus, activeGameGA]);

    const sendGameDesignEvent = useCallback(
        (eventName, value, fieldsJson) =>
            callGameGA('sendDesignEvent', eventName, value, fieldsJson),
        [callGameGA]
    );
    const sendGoogleBasicEvent = useCallback(
        (category, action, value, options) =>
            callGA('basicGaEvent', category, action, value, options),
        [callGA]
    );
    const sendGoogleEvent = useCallback(
        (name, params) => callGA('gaEvent', name, params),
        [callGA]
    );

    useLandingAnalytics({
        sendGameDesignEvent,
        sendGoogleBasicEvent,
        sendGoogleEvent,
        locale,
        pwaStatus,
        pushStatus,
        storageKey,
        lpVersion,
        analyticsConsent,
    });

    const contextValue = useMemo(
        () => ({
            ga: activeGA,
            gameGA: activeGameGA,
            sendGameDesignEvent,
            sendGoogleBasicEvent,
            sendGoogleEvent,
        }),
        [activeGA, activeGameGA, sendGameDesignEvent, sendGoogleBasicEvent, sendGoogleEvent]
    );

    return <AnalyticsContext value={contextValue}>{children}</AnalyticsContext>;
};

export { AnalyticsProvider, AnalyticsContext, useAnalytics };
